/**
 * IVSVideoPlayer - HLS Video Player Component
 *
 * Plays live video streams using HLS.js for cross-browser support.
 * Designed for Amazon IVS streams but works with any HLS source.
 * Falls back to static image when stream is unavailable.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, Maximize, Loader2, WifiOff, Video } from 'lucide-react';
import { cn } from '../lib/utils';

interface IVSVideoPlayerProps {
  streamUrl?: string;
  fallbackImage?: string;
  isLive?: boolean;
  autoplay?: boolean;
  muted?: boolean;
  showControls?: boolean;
  onViewerJoined?: () => void;
  onViewerLeft?: () => void;
  className?: string;
}

export function IVSVideoPlayer({
  streamUrl,
  fallbackImage = '/placeholder.jpg',
  isLive = false,
  autoplay = true,
  muted = true,
  showControls = true,
  onViewerJoined,
  onViewerLeft,
  className,
}: IVSVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hlsRef = useRef<any>(null);

  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isMuted, setIsMuted] = useState(muted);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showingControls, setShowingControls] = useState(false);

  // Dynamically load HLS.js
  useEffect(() => {
    if (!streamUrl || !videoRef.current) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let hls: any = null;

    const initPlayer = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // Check if HLS is supported natively (Safari)
        if (videoRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
          videoRef.current.src = streamUrl;
          setIsLoading(false);
          if (autoplay) {
            videoRef.current.play().catch(() => {});
          }
          return;
        }

        // Use HLS.js for other browsers
        const Hls = (await import('hls.js')).default;

        if (Hls.isSupported()) {
          hls = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 90,
          });

          hls.loadSource(streamUrl);
          hls.attachMedia(videoRef.current!);

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setIsLoading(false);
            if (autoplay) {
              videoRef.current?.play().catch(() => {});
            }
          });

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          hls.on(Hls.Events.ERROR, (_: unknown, data: any) => {
            if (data.fatal) {
              setHasError(true);
              setIsLoading(false);
            }
          });

          hlsRef.current = hls;
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch {
        // HLS initialization failed
        setHasError(true);
        setIsLoading(false);
      }
    };

    initPlayer();

    // Report viewer joined
    onViewerJoined?.();

    return () => {
      if (hls) {
        hls.destroy();
      }
      hlsRef.current = null;
      // Report viewer left
      onViewerLeft?.();
    };
  }, [streamUrl, autoplay, onViewerJoined, onViewerLeft]);

  // Handle play/pause
  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  // Handle mute/unmute
  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Handle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  };

  // Handle video events
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleLoadedData = () => setIsLoading(false);
  const handleError = () => setHasError(true);

  // Show controls on hover/touch
  const handleShowControls = () => setShowingControls(true);
  const handleHideControls = () => setShowingControls(false);

  // If no stream URL or error, show fallback image
  if (!streamUrl || hasError) {
    return (
      <div className={cn('relative w-full h-full bg-black', className)}>
        <img
          src={fallbackImage}
          alt="Stream unavailable"
          className="w-full h-full object-cover"
        />
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50">
            <WifiOff className="w-12 h-12 text-white/50 mb-2" />
            <p className="text-white/70 text-sm">Stream unavailable</p>
          </div>
        )}
        {!isLive && !hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
            <Video className="w-12 h-12 text-white/50 mb-2" />
            <p className="text-white/70 text-sm">Stream not started</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full bg-black', className)}
      onMouseEnter={handleShowControls}
      onMouseLeave={handleHideControls}
      onTouchStart={handleShowControls}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        playsInline
        muted={isMuted}
        autoPlay={autoplay}
        onPlay={handlePlay}
        onPause={handlePause}
        onLoadedData={handleLoadedData}
        onError={handleError}
      />

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-black/50"
          >
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls Overlay */}
      {showControls && (
        <AnimatePresence>
          {(showingControls || !isPlaying) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none"
            >
              {/* Bottom Controls */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-4 pointer-events-auto">
                {/* Play/Pause */}
                <button
                  onClick={togglePlay}
                  className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white" />
                  )}
                </button>

                {/* Mute/Unmute */}
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>

                <div className="flex-1" />

                {/* Fullscreen */}
                <button
                  onClick={toggleFullscreen}
                  className="p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
                  aria-label="Fullscreen"
                >
                  <Maximize className="w-5 h-5 text-white" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Live Badge */}
      {isLive && (
        <div className="absolute top-4 left-4 bg-red-600 px-2 py-1 rounded text-white text-xs font-bold uppercase tracking-wide flex items-center gap-1">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
          LIVE
        </div>
      )}
    </div>
  );
}
